import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { QueryParameter } from '../interfaces/queryString';
import { SapiService } from '../services/sapi.service';
import { LocationService } from '../services/location.service';
import { FilterSetService, FilterSet } from '../services/filter-set.service';
import { Subject, Subscription, ReplaySubject } from 'rxjs';
import { LocalStorageService } from 'angular-2-local-storage';
import { Equals } from '../shared/utils/equals.util';
import { OverlayService } from './overlay.service';
import { EntityStoreService } from './entity-store.service';

export enum BbTableSelectOption {
	single, singleNoDeselect, multi, none
}

export interface TableSort {
	sortProperty: string;
	sortDirection: string;
}

export interface TableRow {
	id: string;
	data: any;
	selected?: boolean;
	quickEditing?: boolean;
	odd?: boolean;
	even?: boolean;
	expanded?: boolean;
	deleted?: boolean;
	saveAction?: any;
	[anything: string]: any;
}

export interface TableColumn {
	property: string;
	optional: boolean;
	label?: string;
	initiallyVisible?: boolean;
	visible?: boolean;
}

export interface TableConfig {
	limit: number;
	visibleColumns: string[];
	order?: string[];
}

@Injectable()
export class BbTableService implements OnInit, OnDestroy {

	public selectOption: BbTableSelectOption = BbTableSelectOption.none;
	public totalAmount: number;
	public offset: number;
	public limit: number;
	public maxTotalAmount: number;

	public rowsRetrievedObservable: Subject<TableRow[]>;
	public rowsSortedObservable: ReplaySubject<TableSort>;
	public rowsSelectedObservable: Subject<TableRow[]>;
	public rowDoubleClickedObservable: Subject<TableRow>;
	public offsetChangedObservable: Subject<number>;
	public totalChangedObservable: ReplaySubject<number>;
	public facetFieldsObservable: ReplaySubject<any>;
	public queryObservable: ReplaySubject<string>;
	public limitChangedObservable: Subject<number>;
	public loadingObservable: Subject<boolean>;
	public scrollSelectedIntoViewObservable: Subject<boolean>;
	public columnsChangedObservable: Subject<TableColumn[]>;
	public tableConfig = new ReplaySubject<TableConfig>(1);

	// table configuration
	public columns: TableColumn[] = [];

	disabled = false;
	loading = false;

	private identifier: string;
	private entity: string;
	private sourceData: any[];
	private linkedToUrl: boolean;
	private configurable = false;
	private liveUpdateSubscription: Subscription;

	private rows: TableRow[] = [];
	private selectedRows: TableRow[] = [];
	private lastSelectedRow: TableRow;
	private sortProperty = 'id';
	private sortDirection = 'desc';
	private filterParams: Map<string, string> = new Map<string, string>();
	private filterQuery: string[] = [];
	private locationParams: any = null;
	private querySet: any = null;

	private extraQueryParams: QueryParameter[];

	private rowsLoaded = false;
	private localFilter: string;
	private query: string;
	private baseFilterSet: FilterSet;
	private filterSet: FilterSet;
	private facetField: string;
	private applyingChanges = false;
	private overlayLoading = true;

	// table configuration
	private configRetrieved = false;
	private config: TableConfig;

	// these might be overridden by configuration or the url
	private defaultLimit: number;
	private defaultSortProperty: string;
	private defaultSortDirection: string;

	private defaultOrder: string[];

	private subscriptions: Subscription;
	private locationServiceParams: Subscription;
	private customLocalFilter: any;
	private customFilterFunctionScope: any;

	private currentPage: number;

	constructor(
		private localStorageService: LocalStorageService,
		private locationService: LocationService,
		private sapiService: SapiService,
		private entityStoreService: EntityStoreService,
		private overlayService: OverlayService,
		private filterSetService: FilterSetService
	) {
		this.rowsRetrievedObservable = new Subject<TableRow[]>();
		this.rowsSortedObservable = new ReplaySubject<TableSort>(1);
		this.rowsSelectedObservable = new Subject<TableRow[]>();
		this.rowDoubleClickedObservable = new Subject<TableRow>();
		this.offsetChangedObservable = new Subject<number>();
		this.totalChangedObservable = new ReplaySubject<number>(1);
		this.facetFieldsObservable = new ReplaySubject<any>(1);
		this.queryObservable = new ReplaySubject<string>(1);
		this.limitChangedObservable = new Subject<number>();
		this.columnsChangedObservable = new Subject<TableColumn[]>();
		this.loadingObservable = new Subject<boolean>();
		this.scrollSelectedIntoViewObservable = new Subject<boolean>();
		this.offset = 0;

		this.extraQueryParams = [];

		this.subscriptions = new Subscription();
		this.subscriptions.add(
			this.filterSetService.queryParamsChangedObservable.subscribe((query: string) => {
				if (query) {
					query = decodeURIComponent(query);
				} else {
					query = null;
				}
				if (this.query !== query) {
					this.setQuery(query);
					this.getRows(true);
				}
			})
		);

		this.subscriptions.add(
			this.filterSetService.filterSetChangedObservable.subscribe((filterSet: FilterSet) => {
				if (!Equals.equals(this.filterSet, filterSet)) {
					this.filterSet = JSON.parse(JSON.stringify(filterSet));
					this.applyLocationParams();
					this.getRows(true);
				}
			})
		);

		if (this.limit) {
			const limit = this.limit;
			this.limit = null;
			this.setLimit(limit, true);
		}
	}

	ngOnInit() {
		this.updateQueryParams();
	}

	ngOnDestroy() {
		if (this.locationServiceParams) {
			this.locationServiceParams.unsubscribe();
		}
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
		this.rowsLoaded = false;
	}

	scrollSelectedIntoView() {
		this.scrollSelectedIntoViewObservable.next(true);
	}

	setLiveUpdates(turnOn: boolean): BbTableService {
		if (!this.liveUpdateSubscription && turnOn) {
			// turn on
			this.entityStoreService.enableSocketUpdates('MediaClip');
			this.liveUpdateSubscription = this.sapiService.getSapiChanges().subscribe((changes) => {
				if (this.rows && this.rows.length) {
					const rowRef: any = {};
					this.rows.forEach((row) => {
						if (row && row.data && row.data.id) {
							rowRef[row.data.id] = row;
						}
					});
					if (changes && changes.request === 'get' || changes.request === 'query' || changes.request === 'websocket') {
						if (changes && changes.data && changes.data.length) {
							changes.data.forEach((item: any) => {
								if (item && item.id && rowRef[item.id] && rowRef[item.id].data && item.type === rowRef[item.id].data.type) {
									rowRef[item.id].data = { ...item };
								}
							});
						}
					}
				}
			});
		} else if (this.liveUpdateSubscription && !turnOn) {
			// turn off
			this.liveUpdateSubscription.unsubscribe();
			this.liveUpdateSubscription = null;
		}
		return this;
	}

	linkToUrl(): BbTableService {
		this.linkedToUrl = true;
		this.filterSetService.setLinkedToUrl(true);
		this.subscriptions.add(
			this.locationService.paramsChangedObservable.subscribe((data) => {
				this.applyLocationParams();
				this.getRows();
			})
		);
		return this;
	}

	useOverlayLoading(overlayLoading: boolean): BbTableService {
		this.overlayLoading = overlayLoading;
		return this;
	}

	setConfigurable(configurable: boolean): BbTableService {
		this.configurable = configurable;
		return this;
	}

	isConfigurable(): boolean {
		return this.configurable;
	}

	setIdentifier(identifier: string): BbTableService {
		this.identifier = identifier;
		this.retrieveConfig();
		return this;
	}

	setSelect(selectOption: BbTableSelectOption): BbTableService {
		this.selectOption = selectOption;
		return this;
	}

	getCurrentPage(): number {
		return this.currentPage;
	}

	getSelectedRows(): TableRow[] {
		return this.selectedRows;
	}

	getOffset(): number {
		return this.offset;
	}

	setData(data: any[]): BbTableService {
		this.sourceData = data;
		return this;
	}

	setEntity(entity: string): BbTableService {
		this.entity = entity;
		return this;
	}

	setFacetField(facetField: string): BbTableService {
		this.facetField = facetField;
		return this;
	}

	addExtraQueryParameter(parameter: string, value: string) {
		this.extraQueryParams.push({ name: parameter, values: [value] } as QueryParameter);
		return this;
	}

	removeExtraQueryParameter(parameter: string) {
		this.extraQueryParams = this.extraQueryParams.filter((queryParam) => queryParam.name !== parameter);
	}

	registerColumn(column: TableColumn) {
		if (!this.configRetrieved) {
			this.retrieveConfig();
		}

		if (this.config && this.config.visibleColumns && this.config.visibleColumns.length) {
			// let config determine our visibility
			column.visible = (this.config.visibleColumns.indexOf(column.property) > -1);
		} else {
			// let parent html determine our visibility
			column.visible = !column.optional;
		}

		column.initiallyVisible = !column.optional;
		if (!this.columns.find((col) => col.property === column.property)) {
			this.columns.push(column);

			this.columnsChangedObservable.next(this.columns);
		}

		this.sortColumns();
	}

	updateLabelForColumn(property: string, label: string) {
		this.columns.forEach(column => {
			if (column.property === property) {
				column.label = label;
			}
		});

		this.columnsChangedObservable.next(this.columns);
	}

	setLimit(limit: number, internal = false): BbTableService {
		if (!internal || limit !== this.limit) {
			this.limit = Math.max(1, limit);
			this.limitChangedObservable.next(this.limit);

			if (typeof this.offset !== 'undefined') {

				this.currentPage = Math.floor(this.offset / this.limit) + 1;

				if (this.rowsLoaded) {
					const maxPage = Math.ceil(this.totalAmount / limit);

					if (this.currentPage > maxPage) {
						// the current page doesn't exist anymore, so change to last page
						this.setPage(maxPage);
						this.currentPage = maxPage;
					}
				}

				if (this.currentPage === 1) {

					// re-retrieve data
					if (this.rowsLoaded && !this.applyingChanges) {
						this.getRows(true);
					}
				} else {

					// changed limit means changed offset for the current page
					// this will trigger re-retrieval of data
					this.setOffset((this.currentPage - 1) * this.limit);
				}
			}
			// else... we expect to receivece a setOffset which will trigger a getData
		}

		return this;
	}

	setMaxTotalAmount(maxTotalAmount: number) {
		if (maxTotalAmount > 0) {
			this.maxTotalAmount = maxTotalAmount;
		} else {
			this.maxTotalAmount = undefined;
		}
		return this;
	}

	setBaseFilterSet(filterSet: FilterSet) {
		this.baseFilterSet = filterSet;
		return this;
	}

	setFilterSet(filterSet: FilterSet) { // meant for setting the filterSet directly without URL management
		this.filterSet = filterSet;
		return this;
	}

	setFilterParam(key: string, value: string): BbTableService {
		this.filterParams.set(key, value);
		return this;
	}

	clearFilterParams() {
		this.filterParams.clear();
	}

	setFilterQuery(query: string | string[]): BbTableService {
		let filterQuery: string[] = [];
		if (query) {
			if (typeof query === 'string') {
				filterQuery = [query];
			} else {
				filterQuery = query;
			}
		}
		this.filterQuery = filterQuery;
		return this;
	}

	setQuery(query: string): BbTableService {
		this.query = query;
		if (!this.query) {
			this.query = null;
		}
		return this;
	}

	setColumnVisibility(property: string, visible: boolean) {
		let amountOfVisible = 0;

		this.columns.forEach(column => {
			if (column.visible) {
				amountOfVisible++;
			}
		});

		if (visible || amountOfVisible > 1) {
			// we don't allow hiding the last column

			this.columns.forEach(column => {
				if (column.property === property) {
					column.visible = visible;
				}
			});
		}

		this.storeConfig();
		this.columnsChangedObservable.next(this.columns);
	}

	setOffset(offset: number) {

		// make sure we are not out of bounds
		offset = Math.max(0, offset);
		if (offset !== offset) {
			offset = 0;
		}

		if (this.offset !== offset) {
			this.offsetChangedObservable.next(offset);
		}

		this.offset = offset;

		if (this.rowsLoaded && !this.applyingChanges) {
			this.getRows(true);
		}
	}

	setPage(page: number) {

		const offset = this.getOffsetForPage(page);

		if (offset === this.offset) {
			// no change necessary
			return;
		}

		this.setOffset(offset);
		this.currentPage = page;

		if (this.linkedToUrl) {

			if (page === 1) {
				// no need for page/limit in the url
				this.locationService.removeParam('page');
				this.locationService.removeParam('limit');
			} else if (!this.applyingChanges) {

				// we are only showing the page + offset in the url, if we are not on page 1, or if it's a manual action
				this.locationService.setParams({ page, limit: this.limit });
			}
		}
	}

	setDisabled(disabled = true): BbTableService {
		this.disabled = disabled;
		return this;
	}

	setLocalFilterFunction(fn: any, scope: any) {
		this.customFilterFunctionScope = scope;
		this.customLocalFilter = fn;
	}

	setLocalFilter(filter: string): BbTableService {
		this.localFilter = filter;

		if (this.rows.length) {

			if (this.localFilter) {
				const filteredRows: TableRow[] = [];

				this.rows.forEach(row => {
					let match = false;
					if (this.customLocalFilter) {
						match = this.customLocalFilter.apply(this.customFilterFunctionScope, [row, filter]);
					} else {
						for (const key in row.data) {

							if (typeof row.data[key] === 'string' && row.data[key].indexOf(this.localFilter) > -1) {
								match = true;
							}
						}
					}


					if (match) {
						filteredRows.push(row);
					}
				});

				this.rowsRetrievedObservable.next(filteredRows);
			} else {
				this.rowsRetrievedObservable.next(this.rows);
			}
		}

		return this;
	}

	selectAllRows() {

		if (this.selectOption === BbTableSelectOption.none || this.selectOption === BbTableSelectOption.single
			|| this.selectOption === BbTableSelectOption.singleNoDeselect) {

			return; // ignore
		}

		if (this.selectedRows.length === this.rows.length) {

			// deselect all
			this.selectedRows = [];

			this.rows.forEach(row => {
				row.selected = false;
			});

		} else {

			// select all
			this.selectedRows = [];

			this.rows.forEach(row => {
				row.selected = true;
				this.selectedRows.push(row);
			});
		}

		this.rowsSelectedObservable.next(this.selectedRows);
	}

	deselectAllRows() {
		this.selectedRows = [];

		this.rows.forEach(row => {
			row.selected = false;
		});

		this.rowsSelectedObservable.next(this.selectedRows);
	}

	getRow(id: string): TableRow {

		let rowForId: TableRow;

		this.rows.forEach((row, index) => {

			if (row.id === id) {
				rowForId = row;
			}
		});

		return rowForId;
	}

	getRowData(id: string): any {

		let rowData: any;

		this.rows.forEach((row, index) => {

			if (row.id === id) {
				rowData = row.data;
			}
		});

		return rowData;
	}

	setRowData(id: string, rowData: any) {
		const newRows = [...this.rows]; // deep copy

		newRows.forEach((row, index) => {
			if (row.id === id) {
				row.data = rowData;
			}
		});
		this.rows = newRows;

		this.rowsRetrievedObservable.next(this.rows);
	}

	selectRow(id: string, allBetween = false, selectOnCtrl = false) {

		if (this.selectOption === BbTableSelectOption.none) {
			return; // ignore
		}
		const previousSelectionCount = this.selectedRows.length;
		this.selectedRows = [];

		if (allBetween && this.selectOption === BbTableSelectOption.multi) {

			// check all between this and last
			let between = false;

			this.rows.forEach((row, index) => {

				if (row === this.lastSelectedRow || row.id === id) {
					between = !between;
				}

				if (between) {
					row.selected = true;
				}
			});
		}

		this.rows.forEach((row, index) => {
			if (row.id === id) {
				if (this.selectOption !== BbTableSelectOption.multi) {
					if (this.selectOption !== BbTableSelectOption.singleNoDeselect) {
						row.selected = !row.selected;
					} else {
						row.selected = true;
					}
				} else if (!row.selected) {
					row.selected = true;
				} else if (previousSelectionCount > 1) {
					row.selected = !selectOnCtrl;
				} else {
					row.selected = false;
				}

				if (row.selected) {
					this.lastSelectedRow = row;
				}
			} else if (this.selectOption === BbTableSelectOption.single || this.selectOption === BbTableSelectOption.singleNoDeselect
				|| (!allBetween && !selectOnCtrl)) {
				row.selected = false; // disable other rows
			}

			if (row.selected) {
				this.selectedRows.push(row);
			}
		});

		this.rowsSelectedObservable.next(this.selectedRows);
	}

	doubleClickRow(id: string) {
		const row: TableRow = this.getRow(id);
		this.rowDoubleClickedObservable.next(row);
	}

	removeRows(ids: string[]) {
		if (!ids) {
			return;
		}

		let filteredSourceData: any[] = [];
		let newTotal = this.totalAmount;
		let removeIds: string[] = [];

		removeIds = ids.map((id: any) => {
			if (id && id.hasOwnProperty('id')) {
				return id.id;
			}
			return id;
		});

		filteredSourceData = this.sourceData.filter((data) => removeIds.indexOf(data.id) === -1);

		if (filteredSourceData) {
			newTotal = this.totalAmount - this.sourceData.length + filteredSourceData.length;
		}

		if (newTotal !== this.totalAmount) {

			if (filteredSourceData.length === 0 && this.offset > 0) {

				// this remove action leaves the current page empty, so load previous one
				this.totalAmount = newTotal;
				this.totalChangedObservable.next(this.totalAmount);

				const currentPage = Math.floor(this.offset / this.limit) + 1;
				this.setPage(currentPage - 1);
				return;
			}

			if (newTotal > (this.offset + filteredSourceData.length) && this.entity) {

				// retrieve new data
				const amount_deleted = (this.totalAmount - newTotal);

				let queryParams: QueryParameter[] = [];

				queryParams = this.updateQueryParams();

				const offset = (this.offset + this.limit - amount_deleted + 1);
				const amount = amount_deleted;

				this.sapiService.query(this.entity, queryParams, offset, amount, this.sortProperty, this.sortDirection)
					.subscribe(data => {
						this.sourceData = [...filteredSourceData, ...data.items];
						this.rows = this.createRowsForData(this.sourceData);
						this.rowsRetrievedObservable.next(this.rows);

						if (data.totalAmount !== this.totalAmount) {
							this.totalAmount = data.totalAmount;
							this.totalChangedObservable.next(this.totalAmount);
						}
					});
			} else {
				this.sourceData = filteredSourceData;
				this.rows = this.createRowsForData(this.sourceData);
				this.rowsRetrievedObservable.next(this.rows);

				if (newTotal !== this.totalAmount) {
					this.totalAmount = newTotal;
					this.totalChangedObservable.next(this.totalAmount);
				}
			}
		}
		this.deselectAllRows();
	}

	sort(sortProperty: string, sortDirection?: string): BbTableService {
		this.defaultSortProperty = sortProperty ? sortProperty.toLowerCase() : 'createddate';
		this.defaultSortDirection = sortDirection ? sortDirection.toLowerCase() : 'desc';
		return this;
	}

	setSort(sort: TableSort): BbTableService {
		this.sortProperty = sort.sortProperty ? sort.sortProperty.toLowerCase() : 'createddate';
		this.sortDirection = sort.sortDirection ? sort.sortDirection.toLowerCase() : 'desc';
		return this;
	}

	updateSortDirection(sortDirection: string) {
		this.sortDirection = sortDirection;
		return this;
	}

	updateSort(sortProperty: string, keepSortDirection: boolean = false): BbTableService {
		if (!sortProperty) {
			sortProperty = this.defaultSortProperty;
		}

		this.sortProperty = sortProperty;

		if (!keepSortDirection) {
			if (this.sortProperty.toLowerCase() === sortProperty.toLowerCase()) {
				// reverse current
				this.sortDirection = (this.sortDirection === 'desc' ? 'asc' : 'desc');
			}
		}

		if (!this.sortDirection) {
			this.sortDirection = this.defaultSortDirection;
		}

		if (keepSortDirection) {
			return this;
		}

		if (this.rowsLoaded && (!this.linkedToUrl || !this.applyingChanges)) {
			this.getRows();
		}

		if (this.linkedToUrl && !this.applyingChanges) {
			this.updateLocationSort();
		}

		return this;
	}

	updateLocationSort() {
		const tableSort: TableSort = { sortDirection: this.sortDirection, sortProperty: this.sortProperty };
		if (this.sortProperty !== this.defaultSortProperty || this.sortDirection !== this.defaultSortDirection) {
			if (this.sortDirection !== this.getLocationParameter('sortDirection') ||
				this.sortProperty !== this.getLocationParameter('sortProperty')) {

				this.locationService.setParams({
					sortDirection: this.sortDirection,
					sortProperty: this.sortProperty
				});
			}
		} else {
			if (this.getLocationParameter('sortProperty')) {
				// no need to have the default sort and direction in the url
				this.locationService.removeParam('sortDirection');
				this.locationService.removeParam('sortProperty');
			}
		}
		this.rowsSortedObservable.next(tableSort);
	}

	getLocationParameter(name: string) {
		if (this.locationService.getParam(name) && this.locationService.getParam(name).values.length) {
			return this.locationService.getParam(name).values[0];
		}
		return null;
	}

	getTotalAmount(): number {
		return this.totalAmount;
	}

	getLimit(): number {
		return this.limit;
	}

	applyConfiguration() {

		if (!this.configRetrieved) {
			this.retrieveConfig();
		}

		if (this.config && this.config.limit) {
			this.setLimit(this.config.limit, true);
		}
	}

	applyLocationParams() {
		if (!this.linkedToUrl) {
			return;
		}

		const params = this.locationService.getParams();
		if (this.objectsEqual(this.locationParams, params)) {
			return;
		}
		let query = null;
		let page = 1;
		let limit = this.limit;
		let sortProperty = this.defaultSortProperty || null;
		let sortDirection = this.defaultSortDirection || null;

		for (const param of params) {
			const name = param.name;
			const values = param.values;

			switch (name) {
				case 'page':
					page = parseInt(values[0], 10);
					break;
				case 'limit':
					limit = parseInt(values[0], 10);
					break;
				case 'q':
					query = decodeURIComponent(values.join(' AND '));
					break;
				case 'sortProperty':
					if (values[0] !== this.defaultSortProperty) {
						sortProperty = values[0];
					}
					break;
				case 'sortDirection':
					sortDirection = values[0];
					break;
				default:
					break;
			}
		}

		// temporarily disable the updating of the url, and the retrieving of new data
		this.applyingChanges = true;
		// console.warn('sortProperty', sortProperty, 'sortDirection', sortDirection)
		if (sortProperty) {
			this.updateSort(sortProperty, sortDirection === this.sortDirection);
		}
		this.rowsSortedObservable.next({ sortProperty, sortDirection });

		this.setQuery(query);
		this.setLimit(limit, true);
		this.setPage(page);
		this.locationParams = params;

		this.applyingChanges = false;
	}

	updateQueryParams(): QueryParameter[] {
		let queryParams: QueryParameter[] = [];

		if (this.extraQueryParams.length) {
			queryParams = [...this.extraQueryParams];
		}

		this.filterParams.forEach((value, key) => {
			queryParams.push({ name: key, values: [value] } as QueryParameter);
		});

		if (this.facetField) {
			queryParams.push({ name: 'facetfield', values: [this.facetField] } as QueryParameter);
		}

		// Workaround to use multiple fq[]'s on the network request
		if (this.filterQuery && this.filterQuery.length > 0) {
			this.filterQuery.forEach((filter: string, index: number) => {
				queryParams.push({ name: 'fq[' + index + ']', values: [filter] } as QueryParameter);
			});
		}

		return queryParams;
	}

	objectsEqual(obj1: any, obj2: any) {
		const eq = obj1 && obj2 && JSON.stringify(obj1) === JSON.stringify(obj2);
		// console.warn('objectsEqual', eq, obj1, obj2)
		return eq;
	}

	getRows(force = false) {
		if (!this.rowsLoaded) {
			// before retrieving data, we might want to apply stuff from configuration and/or the url (the latter takes precedence)

			this.defaultLimit = this.limit;
			// this.defaultOffset = this.offset;
			if (this.linkedToUrl) {
				this.setQuery(this.locationService.getParam('q') ? this.locationService.getParam('q').values.join(',') : '');
			}

			this.applyConfiguration();
		}

		let rows: TableRow[];

		const previousTotalAmount = this.totalAmount;

		if (this.entity && (!this.rowsLoaded || this.totalAmount > this.limit || force)) {
			this.loading = true;
			if (this.overlayLoading) {
				this.overlayService.setLoading(true);
			}

			const queryParams: QueryParameter[] = this.updateQueryParams();

			const filterSet = this.filterSetService.addQueryToFilterSet(this.query, this.filterSet);

			const querySet = {
				entity: this.entity,
				queryParams,
				offset: this.offset,
				limit: this.limit,
				sortProperty: this.sortProperty,
				sortDirection: this.sortDirection,
				filterSet
			};
			if (!force && this.objectsEqual(this.querySet, querySet)) {
				if (this.overlayLoading) {
					this.overlayService.setLoading(false);
				}
				return;
			}
			this.querySet = querySet;
			const fs = filterSet ? [...filterSet] : [];

			fs.forEach((filterGroup) => {
				filterGroup.filters.forEach((filter) => {
					if (filter.value && typeof filter.value !== 'string') {
						filter.value.forEach((key, index, valueArray) => {
							if (filter.value[index].startsWith('-')) {
								filter.field = '-' + filter.field;
								valueArray[index] = filter.value[index].replace('-', '');
							}
						});
					}
				});
			});

			let limit: number = this.limit;
			if (this.maxTotalAmount !== undefined && this.maxTotalAmount !== null && this.maxTotalAmount > -1 && this.maxTotalAmount < this.limit) {
				limit = this.maxTotalAmount;
			}

			this.sapiService.query(
				this.entity,
				queryParams,
				this.offset,
				limit,
				this.sortProperty,
				this.sortDirection,
				(this.baseFilterSet ? [...fs, ...this.baseFilterSet] : fs)
			).subscribe(data => {

				const previousTotal = this.totalAmount;
				if (this.maxTotalAmount !== undefined && this.maxTotalAmount !== null && this.maxTotalAmount > -1 && this.maxTotalAmount < data.totalAmount) {
					this.totalAmount = this.maxTotalAmount;
				} else {
					this.totalAmount = data.totalAmount;
				}

				this.rowsLoaded = true;

				this.sourceData = data.items;

				if (this.totalAmount !== previousTotal) {
					this.totalChangedObservable.next(this.totalAmount);
				}

				if (this.facetField && data.facets) {
					this.facetFieldsObservable.next(data.facets);
				}

				if (data.query) {
					this.queryObservable.next(data.query);
				}

				if (data.items.length === 0 && this.totalAmount > 0) {
					// no results on this page, go to last page which should contain items
					const maxPage = Math.ceil(this.totalAmount / this.limit);
					this.setPage(maxPage);
					return;
				}

				rows = this.createRowsForData(data.items);
				this.rowsRetrievedObservable.next(rows);

				this.loading = false;
				if (this.overlayLoading) {
					this.overlayService.setLoading(false);
				}
			});
		} else {
			if (!this.sourceData) {
				return;
			}
			if (typeof this.totalAmount === 'undefined') {
				this.totalAmount = this.sourceData.length;
				this.rowsLoaded = true;
			}

			const rowData = this.sourceData.slice();

			rowData.sort((first: any, second: any) => {

				let a; let b;

				if (this.sortProperty.indexOf('.') > -1) {
					// find information one level deeper
					// for more complicated situations, it will probably be better to delegate to the calling component the sorting somehow
					const [obj, prop] = this.sortProperty.split('.');

					a = (first[obj] && typeof first[obj][prop] !== 'undefined' ? first[obj][prop] : '');
					b = (second[obj] && typeof second[obj][prop] !== 'undefined' ? second[obj][prop] : '');
				} else {
					a = (typeof first[this.sortProperty] !== 'undefined' ? first[this.sortProperty] : '');
					b = (typeof second[this.sortProperty] !== 'undefined' ? second[this.sortProperty] : '');
				}

				if (typeof a === 'string') {
					a = a.toLowerCase();
				}

				if (typeof b === 'string') {
					b = b.toLowerCase();
				}

				if (a < b) {
					return (this.sortDirection === 'asc' ? -1 : 1);
				} else if (a > b) {
					return (this.sortDirection === 'asc' ? 1 : -1);
				} else {
					return 0;
				}
			});

			this.sourceData = rowData;
			rows = this.createRowsForData(rowData, this.offset, this.limit);
			this.rowsRetrievedObservable.next(rows);

			if (this.totalAmount !== previousTotalAmount) {
				this.totalChangedObservable.next(this.totalAmount);
			}
		}
	}

	resetLimit() {
		this.setLimit(this.defaultLimit); // this will trigger re-retrieval if necessasry
		this.storeConfig();
	}

	public setDefaultOrder(order: string[]) {
		this.defaultOrder = order;
		return this;
	}

	resetColumns() {

		this.columns.forEach(column => {
			column.visible = column.initiallyVisible;
		});

		this.columnsChangedObservable.next(this.columns);
		this.storeConfig(this.defaultOrder);
	}

	storeConfig(order?: string[]) {
		const tableConfig = {
			limit: this.limit,
			visibleColumns: [],
			order: order || this.config?.order || this.defaultOrder || []
		} as TableConfig;

		this.columns.forEach(column => {
			if (column.visible) {
				tableConfig.visibleColumns.push(column.property);
			}
		});

		if (this.identifier) {
			this.localStorageService.set('ovp-' + this.identifier + '-config', JSON.stringify(tableConfig));
			this.retrieveConfig();
		}
	}

	private sortColumns(): void {
		if (!this.config?.order) {
			return; // no custom order set
		}

		const sorted: TableColumn[] = [];

		// make sure column in config is in current options
		this.config.order.forEach(columnName => {
			const found = this.columns.find(tc => tc.property === columnName);
			if (found) {
				sorted.push(found);
			}
		});

		// add missing (added later than the config created) atthe end
		this.columns.forEach(defaultColumn => {
			const found = sorted.find(s => s.property === defaultColumn.property);
			if (!found) {
				sorted.push(defaultColumn);
			}
		});

		this.columns = sorted;
		this.columnsChangedObservable.next(this.columns);
	}

	private getOffsetForPage(page: number): number {
		return (page - 1) * this.limit;
	}

	private retrieveConfig() {
		if (this.identifier) {
			const configJson = this.localStorageService.get('ovp-' + this.identifier + '-config');

			if (configJson) {
				this.config = JSON.parse(configJson as string);
				this.tableConfig.next(this.config);
			}
			this.configRetrieved = true;
			this.sortColumns();
		}
	}

	private createRowsForData(rowData: any[], offset = 0, limit = rowData.length): TableRow[] {

		this.rows = [];

		for (let i = offset; i < offset + limit; i++) {
			const data = rowData[i];

			if (data) {
				this.rows.push({
					id: (data.id || i),
					odd: (i % 2 === 0),
					even: (i % 2 !== 0),
					data
				});
			}
		}

		return this.rows;
	}

}
